import { createId } from '@paralleldrive/cuid2';
import { useState } from 'react';

import { CreateOrderDTO, OrderCurrency } from '@invenco/sales-interface/interfaces';

import { WizardStep } from '@/components/wizard';
import type { WizardStepDefinition } from '@/components/wizard/wizard-context';

import {
  EditableOrderLinesCard,
  type OrderLine,
  type OrderLineAddInput,
  type OrderLineUpdatePatch,
} from '../order-lines-card';

type ItemsStepProps = {
  step: WizardStepDefinition;
  currency: OrderCurrency;
  onChange: (input: Partial<CreateOrderDTO>) => void;
};

export function ItemsStep({ step, currency, onChange }: ItemsStepProps) {
  const [lines, setLines] = useState<OrderLine[]>([]);

  const submitSection = () => {
    if (!lines.length) return false;

    onChange({
      orderLines: lines.map(
        ({ skuName, description, qtyOrdered, unitPrice, discountAmount, discountRate }) => ({
          skuName,
          description,
          qtyOrdered,
          unitPrice,
          discountAmount: discountAmount ?? undefined,
          discountRate: discountRate ?? undefined,
        }),
      ),
    });
    return true;
  };

  const addLine = async (line: OrderLineAddInput) => {
    setLines((currentLines) => [...currentLines, { id: createId(), ...line }]);
  };

  const updateLine = async (lineId: string, patch: OrderLineUpdatePatch) => {
    setLines((currentLines) =>
      currentLines.map((line) => (line.id === lineId ? { ...line, ...patch } : line)),
    );
  };

  const deleteLine = async (lineId: string) => {
    setLines((currentLines) => currentLines.filter((line) => line.id !== lineId));
  };

  return (
    <WizardStep step={step} complete={lines.length > 0} onSubmitStep={submitSection}>
      <EditableOrderLinesCard
        title="Add item(s)"
        emptyMessage={null}
        lines={lines}
        order={{ currency }}
        onAddLine={addLine}
        onUpdateLine={updateLine}
        onDeleteLine={deleteLine}
      />
    </WizardStep>
  );
}
